import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '../components/Icon';
import { api } from '../lib/api';
import { date, relativeDays } from '../lib/format';
import { useSession } from '../lib/session';
import '../styles/compliance.css';

interface ComplianceDoc {
  id: string;
  docType: string;
  docLabel: string;
  number: string | null;
  issuer: string | null;
  country: string | null;
  holder: string;
  issueDate: string | null;
  expiryDate: string | null;
  daysToExpiry: number | null;
  threshold: number | null;
  severity: 'info' | 'warning' | 'critical';
  status: string;
}

interface Alert {
  id: string;
  kind: string;
  severity: string;
  title: string;
  body: string;
  daysToDue: number | null;
  entityId: string | null;
}

const THRESHOLDS = [120, 90, 60, 30, 15, 7, 0];

const sevTone = (s: string): 'active' | 'warn' | 'broken' | 'muted' =>
  s === 'critical' ? 'broken' : s === 'warning' ? 'warn' : 'muted';

const expiryColor = (days: number | null): string => {
  if (days == null) return 'var(--line-strong)';
  if (days < 0) return 'var(--clay)';
  if (days <= 30) return 'var(--clay)';
  if (days <= 60) return 'var(--amber)';
  return 'var(--accent)';
};

function ExpiryCell({ doc }: { doc: ComplianceDoc }) {
  const days = doc.daysToExpiry;
  // Regla visual: 180 días de horizonte; cuanto menos queda, más llena la barra.
  const pct = days == null ? 0 : Math.max(4, Math.min(100, ((180 - days) / 180) * 100));
  return (
    <div className="cexp">
      <span className="cexp__date">{date(doc.expiryDate)}</span>
      <span className="cexp__bar">
        <span className="cexp__fill" style={{ width: `${pct}%`, background: expiryColor(days) }} />
      </span>
      <span className="cexp__days" style={{ color: expiryColor(days) }}>{relativeDays(days)}</span>
    </div>
  );
}

export function Compliance() {
  const loc = useLocation();
  const nav = useNavigate();
  const { company } = useSession();
  const mode = loc.pathname.includes('vencimientos') ? 'expiries' : 'registry';

  const [docs, setDocs] = useState<ComplianceDoc[] | null>(null);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!company) return;
    setDocs(null);
    setErr(null);
    api.get<ComplianceDoc[]>('/compliance').then(setDocs).catch((e) => setErr(e instanceof Error ? e.message : 'Error'));
    if (mode === 'expiries') {
      api.get<Alert[]>('/alerts').then(setAlerts).catch(() => void 0);
    }
  }, [company, mode]);

  const sorted = useMemo(() => {
    if (!docs) return [];
    const copy = docs.slice();
    if (mode === 'expiries') {
      copy.sort((a, b) => (a.daysToExpiry ?? 9999) - (b.daysToExpiry ?? 9999));
    }
    return copy;
  }, [docs, mode]);

  // Para la escala de umbrales: el mínimo de días restantes entre todos los docs.
  const nearestDays = useMemo(() => {
    const vals = (docs ?? []).map((d) => d.daysToExpiry).filter((x): x is number => x != null);
    return vals.length ? Math.min(...vals) : null;
  }, [docs]);

  return (
    <main className="main">
      <div className="page-head">
        <div className="page-head__title">
          <span className="eyebrow">Cumplimiento</span>
          <h1 className="h1">{mode === 'expiries' ? 'Vencimientos' : 'Licencias y visas'}</h1>
          <p className="page-head__sub">
            {mode === 'expiries'
              ? 'Everything that expires, ordered by urgency. Alerts trigger at each threshold: 120, 90, 60, 30, 15, 7 and 0 days.'
              : 'Registro de licencias comerciales, visas y documentos oficiales. Cada uno con su caducidad y el umbral de aviso que ya ha cruzado.'}
          </p>
        </div>
      </div>

      {/* Escala de umbrales */}
      {nearestDays != null && (
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="thr-scale">
            <span className="thr-scale__label">Umbrales de aviso</span>
            {THRESHOLDS.map((t) => {
              const passed = nearestDays <= t;
              const isNext = passed && THRESHOLDS.filter((x) => nearestDays <= x).sort((a, b) => a - b)[0] === t;
              return (
                <span key={t} className={`thr-mark${passed ? (isNext ? ' thr-mark--next' : ' thr-mark--passed') : ''}`}>
                  {t} d
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Alertas (solo en modo vencimientos) */}
      {mode === 'expiries' && alerts.length > 0 && (
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card__head"><span className="card__title">Avisos activos</span></div>
          <div className="card__body card__body--flush">
            {alerts.map((a) => (
              <button
                key={a.id}
                className="alert-item"
                style={{ width: '100%', textAlign: 'left', cursor: a.entityId ? 'pointer' : 'default' }}
                onClick={() => a.entityId && nav(`/documentos/${a.entityId}`)}
              >
                <span className={`alert-item__bar alert-item__bar--${a.severity}`} />
                <div>
                  <div className="alert-item__title">{a.title}</div>
                  <div className="alert-item__body">{a.body}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        {err && <div className="empty">{err}</div>}
        {!err && !docs && <div className="empty">Loading…</div>}
        {!err && docs && sorted.length === 0 && (
          <div className="empty">No compliance documents recorded yet.</div>
        )}

        {!err && sorted.length > 0 && (
          <>
            <div className="ctable__head">
              <span>Documento</span>
              <span>Titular</span>
              <span>Emisor</span>
              <span>Caducidad</span>
              <span>Estado</span>
            </div>
            {sorted.map((d) => (
              <div key={d.id} className="ctable__row">
                <span className="cdoc">
                  <span className="cdoc__ico"><Icon name="shield" size={17} /></span>
                  <span>
                    <span className="cdoc__name">{d.docLabel}</span>
                    <span className="cdoc__num" style={{ display: 'block' }}>{d.number ?? '—'}</span>
                  </span>
                </span>
                <span style={{ fontSize: 13 }}>{d.holder}</span>
                <span style={{ fontSize: 12.5, color: 'var(--graphite)' }}>
                  {d.issuer ?? '—'}{d.country ? ` · ${d.country}` : ''}
                </span>
                <ExpiryCell doc={d} />
                <span className="row gap-8 center">
                  <span className={`pill pill--${sevTone(d.severity)}`}>
                    <span className="pill__dot" />
                    {d.status === 'expired' ? 'Caducado' : d.severity === 'critical' ? 'Critical' : d.severity === 'warning' ? 'Next' : 'Vigente'}
                  </span>
                </span>
              </div>
            ))}
          </>
        )}
      </div>
    </main>
  );
}
